import { CSSProperties, FC, useMemo } from 'react';
import clsx from 'clsx';
import { CircadianPhase } from '@demind-inc/core';

import './ZoneLabelTimeline.scss';
import { getGutterEnergyZoneBlockStyle } from '../../../Energy/energyZoneStyles';
import { circadianPhaseInfo, PeakDipStartEndSet } from '../../../../data-access';
import { HOUR_BLOCK_HEIGHT } from '../../CalendarTimeline/constants';
import { getRoundUpDateTime, getRoundDownDateTime } from '../../CalendarTimeline/helpers';
import dayjs from 'dayjs';
import { ZoneLabelExplaTooltipContent } from './ZoneLabelExplaTooltipContent';
import { CustomTooltip } from '../../../common';
import { useGeneralSettings } from '../../../../hooks/useGeneralSettings';
import { min } from 'lodash';
import { useTranslation } from 'react-i18next';
import { getCircadianPhaseTitle } from '../../../DataView/i18n';

interface ZoneLabelTimelineProps {
  peaksDipsBoundaries: PeakDipStartEndSet;
  className?: string;
  disabled?: boolean;
}

const LABEL_WIDTH = 120;

const buildZoneStyle = (startTime: string, endTime: string): CSSProperties => {
  const roundedStartTime = getRoundUpDateTime(dayjs(startTime));
  const roundedEndTime = getRoundDownDateTime(dayjs(endTime));

  const startTimeHour = roundedStartTime.hour() + roundedStartTime.minute() / 60;
  const duration = roundedEndTime.diff(roundedStartTime, 'minutes') / 60;

  const top = Math.round(startTimeHour * HOUR_BLOCK_HEIGHT);
  const height = Math.round(duration * HOUR_BLOCK_HEIGHT);

  return {
    top: `${top}px`,
    height: `${height}px`,
    width: '100%',
  };
};

const buildZoneStyleFromDOM = (index: number): CSSProperties | null => {
  const bgEvents = document.querySelectorAll('.rbc-background-event');
  const bgEvent = bgEvents[index] as HTMLElement;

  if (bgEvent && bgEvent.offsetHeight > 0) {
    const computedStyle = window.getComputedStyle(bgEvent);
    return {
      top: computedStyle.top,
      height: computedStyle.height,
      width: '100%',
    };
  }

  return null;
};

const ZoneLabelTimeline: FC<ZoneLabelTimelineProps> = ({
  peaksDipsBoundaries,
  className,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const isTimeFormat24h = timeFormat === '24h';
  const timeFormatForTooltip = isTimeFormat24h ? 'HH:mm' : 'h:mm A';

  const domStyles = useMemo(() => {
    const styles: Record<string, CSSProperties | null> = {};
    Object.keys(peaksDipsBoundaries).forEach((phase, index) => {
      styles[phase] = buildZoneStyleFromDOM(index);
    });
    return styles;
  }, [peaksDipsBoundaries]);

  return (
    <div className={clsx('zone-label-timeline', className)}>
      {Object.entries(peaksDipsBoundaries)
        .filter(([_, { start, end }]) => start && end)
        .map(([phase, { start, end }], _) => {
          const phaseInfo = circadianPhaseInfo[phase as CircadianPhase];

          const hasNoZone = dayjs(end).diff(start, 'minutes') <= 0;
          if (hasNoZone) {
            return null;
          }

          const defaultStyle = buildZoneStyle(start, end);
          const domStyle = domStyles[phase];

          const zoneColor = phaseInfo?.color || '#680ddb';
          const zoneBlockStyle = getGutterEnergyZoneBlockStyle(zoneColor);
          const style = {
            ...(domStyle || defaultStyle),
            ...zoneBlockStyle,
          };

          const zoneTime = `${dayjs(start).format(timeFormatForTooltip)} - ${dayjs(end).format(timeFormatForTooltip)}`;
          const zoneHeight = parseInt(style.height.toString());

          return (
            <div key={phase}>
              <CustomTooltip
                title={
                  disabled ? null : (
                    <ZoneLabelExplaTooltipContent
                      title={getCircadianPhaseTitle(t, phase, phaseInfo.title)}
                      time={zoneTime}
                      color={phaseInfo.color}
                      explanation={t(`calendar.energyPhase.explanations.${phase}`, {
                        defaultValue: phaseInfo.explanation,
                      })}
                      phase={phase as CircadianPhase}
                      phaseInfoImage={phaseInfo.imagePath}
                    />
                  )
                }
                placement="left"
                enterDelay={600}
                enterNextDelay={600}
                enterTouchDelay={600}
              >
                <div
                  className={clsx('zone-label-timeline__item', {
                    'zone-label-timeline__item--24h': isTimeFormat24h,
                  })}
                  style={style}
                  key={phase}
                >
                  <div
                    className="zone-label-timeline__item__label energy-zone-block__label"
                    style={{
                      backgroundColor: zoneColor,
                      maxWidth: min([zoneHeight - 5, LABEL_WIDTH]),
                    }}
                  >
                    {t(`calendar.energyPhase.labels.${phase}`, {
                      defaultValue: phaseInfo?.label,
                    })}
                    {phaseInfo.labelIcon}{' '}
                  </div>
                </div>
              </CustomTooltip>
            </div>
          );
        })}
    </div>
  );
};

export default ZoneLabelTimeline;
